import React from "react";
import { Crosshair, Plus, Trash2, Shield, Info, Globe, RotateCcw } from "lucide-react";
import type { SowFields, SowTargetItem } from "../../../../../utils/api/endpoints/sow";

const ASSESSMENT_OPTIONS = [
  { value: "WEB_APP", label: "Web Application" },
  { value: "API", label: "API / Integration Service" },
  { value: "MOBILE_APP", label: "Mobile Application (General / Both)" },
  { value: "ANDROID_APP", label: "Android Application" },
  { value: "IOS_APP", label: "iOS Application" },
  { value: "EXTERNAL_NETWORK", label: "External Network" },
  { value: "INTERNAL_NETWORK", label: "Internal Network" },
  { value: "CLOUD_CONFIG", label: "Cloud Configuration Audit" },
  { value: "CLOUD_PENTEST", label: "Cloud Infrastructure Pentest" },
  { value: "AI_LLM", label: "AI & LLM Assessment" },
  { value: "CODE_REVIEW", label: "Secure Code Review" },
  { value: "VPN_FIREWALL_REVIEW", label: "VPN & Firewall Config Review" },
  { value: "M365_REVIEW", label: "M365 Tenant Audit" }
];

const ENV_OPTIONS = ["Production", "Staging", "Development", "UAT", "Pre-Production"];
const STATUS_OPTIONS = ["Confirmed", "In Scope", "Pending Access", "Conditional"];

interface TargetsScopeEditorProps {
  fields: SowFields;
  setField: <K extends keyof SowFields>(key: K, value: SowFields[K]) => void;
  clientName: string;
}

export const TargetsScopeEditor: React.FC<TargetsScopeEditorProps> = ({
  fields,
  setField,
  clientName
}) => {
  const targetItems = fields.targetItems || [];

  const handleUpdateTarget = (id: string, updates: Partial<SowTargetItem>) => {
    const updated = targetItems.map((item) => (item.id === id ? { ...item, ...updates } : item));
    setField("targetItems", updated);
  };

  const handleAddTarget = () => {
    const newId = `tgt-${Date.now()}`;
    const newItem: SowTargetItem = {
      id: newId,
      assetName: "",
      assessmentType: "WEB_APP",
      accessType: "graybox",
      targets: "",
      environment: "Production",
      status: "Confirmed",
      description: ""
    };
    setField("targetItems", [...targetItems, newItem]);
  };

  const handleRemoveTarget = (id: string) => {
    if (targetItems.length <= 1) {
      alert("At least one target row is required in the Authorised Targets table.");
      return;
    }
    const filtered = targetItems.filter((item) => item.id !== id);
    setField("targetItems", filtered);
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-slate-200 pb-4">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <Crosshair size={16} className="text-ctb-blue" />
          Section 3: Authorised Targets & Scope Boundary
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Define every authorised asset, assessment methodology, endpoints, testing environment, and legal boundary. This directly populates Table 1 in the PDF Statement of Work.
        </p>
      </div>

      {/* Target Items Table */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
            <Globe size={13} className="text-ctb-blue" />
            Authorised Targets (Table 1)
          </label>
          <button
            type="button"
            onClick={handleAddTarget}
            className="px-2.5 py-1.5 bg-ctb-blue text-white text-xs font-bold rounded-lg hover:bg-ctb-blue/90 flex items-center gap-1 cursor-pointer transition shadow-sm"
          >
            <Plus size={13} />
            Add Target Row
          </button>
        </div>

        <div className="space-y-3">
          {targetItems.map((item, idx) => (
            <div
              key={item.id}
              className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3 hover:border-slate-300 transition"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold flex items-center justify-center">
                    {idx + 1}
                  </span>
                  {item.assetName ? item.assetName : "New Scoped Asset"}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveTarget(item.id)}
                  disabled={targetItems.length <= 1}
                  className="text-slate-400 hover:text-rose-600 p-1 rounded-md transition disabled:opacity-30 cursor-pointer"
                  title="Remove target"
                >
                  <Trash2 size={13} />
                </button>
              </div>

              <div className="space-y-3">
                {/* Row 1: Asset Name & Assessment Type */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[11px] font-semibold text-slate-600 block">Asset Name</label>
                    <input
                      type="text"
                      value={item.assetName}
                      onChange={(e) => handleUpdateTarget(item.id, { assetName: e.target.value })}
                      placeholder="e.g. Customer Web Portal"
                      className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-2 text-slate-800 font-medium focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[11px] font-semibold text-slate-600 block">Assessment Type</label>
                    <select
                      value={item.assessmentType}
                      onChange={(e) => handleUpdateTarget(item.id, { assessmentType: e.target.value })}
                      className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-2 text-slate-800 font-medium cursor-pointer focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue"
                    >
                      {ASSESSMENT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Row 2: Approach, Environment, Status */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="text-[11px] font-semibold text-slate-600 block">Approach</label>
                    <select
                      value={item.accessType}
                      onChange={(e) =>
                        handleUpdateTarget(item.id, { accessType: e.target.value as "graybox" | "blackbox" | "whitebox" })
                      }
                      className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-2 text-slate-800 font-medium cursor-pointer focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue"
                    >
                      <option value="graybox">Gray-box</option>
                      <option value="blackbox">Black-box</option>
                      <option value="whitebox">White-box</option>
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[11px] font-semibold text-slate-600 block">Environment</label>
                    <select
                      value={item.environment || "Production"}
                      onChange={(e) => handleUpdateTarget(item.id, { environment: e.target.value })}
                      className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-2 text-slate-800 font-medium cursor-pointer focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue"
                    >
                      {ENV_OPTIONS.map((env) => (
                        <option key={env} value={env}>
                          {env}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[11px] font-semibold text-slate-600 block">Status</label>
                    <select
                      value={item.status || "Confirmed"}
                      onChange={(e) => handleUpdateTarget(item.id, { status: e.target.value })}
                      className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-2 text-slate-800 font-medium cursor-pointer focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue"
                    >
                      {STATUS_OPTIONS.map((st) => (
                        <option key={st} value={st}>
                          {st}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-slate-600">Target URLs / IPs / CIDR ranges</label>
                <input
                  type="text"
                  value={item.targets}
                  onChange={(e) => handleUpdateTarget(item.id, { targets: e.target.value })}
                  placeholder="e.g. https://app.example.com, https://api.example.com/v1"
                  className="w-full text-xs font-mono bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-slate-600">Scope Description / Narrative Note</label>
                <input
                  type="text"
                  value={item.description || ""}
                  onChange={(e) => handleUpdateTarget(item.id, { description: e.target.value })}
                  placeholder={`Assessment of ${item.assetName || "the specified target"} across identified endpoints and business workflows.`}
                  className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scope Boundary Narrative */}
      {(() => {
        const clientDisplay = clientName && clientName.trim() ? clientName.trim() : "Client";
        const defaultScopeBoundary = `Testing is strictly limited to the formally authorised targets confirmed by ${clientDisplay} before the engagement begins. Any host, service, application, or address outside the approved target list is out of scope. Individual pages, routes, API paths, parameters, ports, subdomains, and administrative interfaces identified during testing are treated as discovery artefacts for reporting purposes only and do not independently expand the authorised legal scope.`;
        return (
          <div className="space-y-2 pt-2 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <Shield size={13} className="text-amber-600" />
                Scope Boundary and Endpoint Discovery Clause
              </label>
              <button
                type="button"
                onClick={() => setField("scopeBoundaryText", defaultScopeBoundary)}
                className="text-[11px] text-ctb-blue hover:underline flex items-center gap-1 cursor-pointer"
              >
                <RotateCcw size={11} />
                Reset to default
              </button>
            </div>
            <textarea
              rows={4}
              value={fields.scopeBoundaryText ?? ""}
              onChange={(e) => setField("scopeBoundaryText", e.target.value)}
              className="w-full text-xs text-slate-800 border border-slate-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue leading-relaxed font-sans"
            />
            <span className="text-[10px] text-slate-400">
              Protects both parties by establishing strict legal perimeter boundaries for authorized testing.
            </span>
          </div>
        );
      })()}

      {/* Rules of Engagement Summary Narrative */}
      {(() => {
        const defaultRoeSummary = `Appendix A - Rules of Engagement forms part of this Statement of Work and records the authorised targets, testing environment, CTB's testing source address, in-scope accounts and named testers, confirmed testing duration, testing window and blackout periods, emergency contacts, prohibited testing activities, and the stop-testing procedure.

Fields in Appendix A marked as "To be confirmed" depend on information that becomes available during scope validation. They will be completed and confirmed in writing by both parties before testing commences, and completion of Appendix A is a condition precedent to the commencement of testing. Where Appendix A conflicts with the body of this Statement of Work, the body prevails.`;
        return (
          <div className="space-y-2 pt-2 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <Shield size={13} className="text-ctb-blue" />
                Rules of Engagement Scope Clause
              </label>
              <button
                type="button"
                onClick={() => setField("rulesOfEngagementSummaryText", defaultRoeSummary)}
                className="text-[11px] text-ctb-blue hover:underline flex items-center gap-1 cursor-pointer"
              >
                <RotateCcw size={11} />
                Reset to default
              </button>
            </div>
            <textarea
              rows={4}
              value={fields.rulesOfEngagementSummaryText ?? ""}
              onChange={(e) => setField("rulesOfEngagementSummaryText", e.target.value)}
              className="w-full text-xs text-slate-800 border border-slate-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue leading-relaxed font-sans"
            />
            <span className="text-[10px] text-slate-400">
              Explains the operational binding of Appendix A Rules of Engagement with the Statement of Work.
            </span>
          </div>
        );
      })()}
    </div>
  );
};
